import React, { FC, useEffect } from 'react'
import { Heading, Box } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { Region } from '@moonpig/web-core-types'
import { useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { createViewItemListGAEvent } from '@moonpig/web-shared-products'
import { useInView } from 'react-intersection-observer'
import { Carousel } from '@moonpig/web-shared-components'
import { useFindLocaleText } from '../../text-localisation'
import { ProductTileWithAddToBasketHandler } from './ProductTileWithAddToBasketHandler'
import { Product } from '../../types'

type PeopleAlsoViewedCarouselProps = {
  parentProductId: string
  products: Product[]
  region: Region
  carouselLength: number
  carouselIndex: number
  backgroundColor?: string
}

const StyledTileContainer = styled(Box)<{ isFirst: boolean; isLast: boolean }>`
  ${({ isFirst, isLast, theme: { spacing } }) => {
    const tileWidth = 240 // px
    // To avoid clipping the shadow on the first and last tiles
    const extraSpaceOnSides = 4
    return s({
      height: '100%',
      width: `${
        isFirst || isLast ? tileWidth + spacing[extraSpaceOnSides] : tileWidth
      }px`,
      pl: isFirst ? { md: extraSpaceOnSides } : 0,
      pr: isLast ? { md: extraSpaceOnSides } : 0,
    })
  }}
`

export const PeopleAlsoViewedCarousel: FC<PeopleAlsoViewedCarouselProps> = ({
  parentProductId,
  products,
  region,
  carouselLength,
  carouselIndex,
  backgroundColor,
}) => {
  const localiseText = useFindLocaleText()
  const [productListRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  })
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const carouselTrackingInfo = {
    carouselTitle: localiseText('common.people_also_viewed'),
    carouselIndex,
    carouselLength,
  }
  useEffect(() => {
    if (inView) {
      const label = `product details | ${parentProductId} | people also viewed | carousel | ${carouselIndex}/${carouselLength}`
      trackGAEventOnce(
        createViewItemListGAEvent({
          products,
          label,
        }),
      )
    }
  }, [
    products,
    region,
    parentProductId,
    carouselIndex,
    carouselLength,
    inView,
    trackGAEventOnce,
  ])

  return (
    <Box
      ref={productListRef}
      mb={{ xs: 8, md: 10 }}
      data-testid="web-browse-carousel-products-component"
    >
      <Heading
        level="h3"
        mb={4}
        ml={{ xs: 0, md: 10 }}
        typography={{ xs: 'typeMobileDisplay04', md: 'typeDesktopDisplay05' }}
      >
        {localiseText('common.people_also_viewed')}
      </Heading>

      <Box bgcolor={backgroundColor}>
        {/* eslint-disable-next-line no-warning-comments */}
        {/* TODO: Add aria-label for carousel */}
        <Carousel ariaLabel="">
          {products.map((product, index, { length }) => {
            return (
              <StyledTileContainer
                key={product.id}
                isFirst={index === 0}
                isLast={index === length - 1}
              >
                <ProductTileWithAddToBasketHandler
                  product={product}
                  index={index}
                  region={region}
                  parentProductId={parentProductId}
                  productsLength={products.length}
                  carouselTrackingInfo={carouselTrackingInfo}
                />
              </StyledTileContainer>
            )
          })}
        </Carousel>
      </Box>
    </Box>
  )
}
